import dayjs from 'dayjs'

import { GroupByDate } from '@/graphql'
import { DateHelpers } from '@/utils/date.helpers'

const getChartWindow = (start: string, end: string, max: string) => {
  const startDate = dayjs(start)
  const endDate = dayjs(end)
  const diff = endDate.diff(startDate, 'day')
  const numberOfDays = Math.max(14, diff * 3) // we want at least 14 days
  const halfNumberOfDays = Math.floor(numberOfDays / 2)
  const maxDate = dayjs(max)
  // we don't want the end date to be greater than today
  // we want to try to get the input range in the middle of the window
  const middleOfInputRange = startDate.add(diff, 'day')
  const endPotentialEnd = middleOfInputRange.add(halfNumberOfDays, 'day')
  const endWindow = endPotentialEnd.isAfter(maxDate) ? maxDate : endPotentialEnd
  const startWindow = endWindow.subtract(numberOfDays, 'day')
  return {
    startDate: DateHelpers.getStartOfDayWithTimezone(
      startWindow.format('YYYY-MM-DD'),
    ),
    endDate: DateHelpers.getEndOfDayWithTimezone(
      endWindow.format('YYYY-MM-DD'),
    ),
  }
}

const getNumberOfIncompletePeriods = (
  dates: dayjs.Dayjs[],
  dataGranularity: GroupByDate,
): number => {
  if (!dates || dates.length === 0) {
    return 0
  }

  const todayDateStr = dayjs().format('YYYY-MM-DD')

  switch (dataGranularity) {
    case GroupByDate.DAY: {
      return dates.filter(date => {
        const d = new Date(date.valueOf())
        const year = d.getUTCFullYear()
        const month = String(d.getUTCMonth() + 1).padStart(2, '0')
        const day = String(d.getUTCDate()).padStart(2, '0')
        const dateStr = `${year}-${month}-${day}`
        return dateStr >= todayDateStr
      }).length
    }

    case GroupByDate.WEEK: {
      const todayWeekStr = dayjs().startOf('week').format('YYYY-MM-DD')
      return dates.filter(date => {
        const d = new Date(date.valueOf())
        const year = d.getUTCFullYear()
        const month = String(d.getUTCMonth() + 1).padStart(2, '0')
        const day = String(d.getUTCDate()).padStart(2, '0')
        const dateStr = `${year}-${month}-${day}`
        const weekStart = dayjs(dateStr).startOf('week').format('YYYY-MM-DD')
        return weekStart >= todayWeekStr
      }).length
    }

    case GroupByDate.MONTH: {
      const todayMonthStr = dayjs().format('YYYY-MM')
      return dates.filter(date => {
        const d = new Date(date.valueOf())
        const year = d.getUTCFullYear()
        const month = String(d.getUTCMonth() + 1).padStart(2, '0')
        const monthStr = `${year}-${month}`
        return monthStr >= todayMonthStr
      }).length
    }

    case GroupByDate.YEAR: {
      const todayYear = dayjs().year()
      return dates.filter(date => {
        const d = new Date(date.valueOf())
        const year = d.getUTCFullYear()
        return year >= todayYear
      }).length
    }

    default:
      return 0
  }
}

const getCompletionPercentage = (
  dates: dayjs.Dayjs[],
  dataGranularity: GroupByDate,
): number => {
  if (
    !dates ||
    dates.length === 0 ||
    getNumberOfIncompletePeriods(dates, dataGranularity) === 0
  ) {
    return 100
  }

  const startOfDay = dayjs().startOf('day')

  switch (dataGranularity) {
    case GroupByDate.DAY: {
      const elapsedHours = dayjs().diff(startOfDay, 'hour')
      const totalHoursInDay = 24

      return Math.min(100, Math.round((elapsedHours / totalHoursInDay) * 100))
    }

    case GroupByDate.WEEK: {
      const currentDayOfWeek = startOfDay.day()
      const totalDaysInWeek = 7

      return Math.min(
        100,
        Math.round((currentDayOfWeek / totalDaysInWeek) * 100),
      )
    }

    case GroupByDate.MONTH: {
      const currentDayOfMonth = startOfDay.date()
      const totalDaysInMonth = dayjs().daysInMonth()

      return Math.min(
        100,
        Math.round((currentDayOfMonth / totalDaysInMonth) * 100),
      )
    }

    case GroupByDate.YEAR: {
      const elapsedDaysInYear = startOfDay.diff(dayjs().startOf('year'), 'day')
      return Math.min(100, Math.round((elapsedDaysInYear / 365) * 100))
    }

    default:
      return 100
  }
}

const getXAxisRangeAnnotation = (
  t: (t: string) => string,
  startDate?: string,
  endDate?: string,
  granularity?: GroupByDate,
): XAxisAnnotations[] => {
  if (startDate && endDate && granularity) {
    const s = dayjs(startDate)
    const e = dayjs(endDate)
    const today = dayjs()
    const isSameDay = s.isSame(e, 'day')
    const isToday = s.isSame(today, 'day') && isSameDay
    const isYesterday = s.isSame(today.subtract(1, 'day'), 'day') && isSameDay
    const firstText = isToday
      ? t('common.today')
      : isYesterday
        ? t('common.yesterday')
        : isSameDay
          ? t('common.selectedDate')
          : t('common.startDate')

    const dateFormat = getShortDateFormatFromGroupBy(granularity)
    const startFormatted = dayjs(startDate).format(dateFormat)
    const endFormatted = dayjs(endDate).format(dateFormat)

    return [
      {
        x: startFormatted,
        borderColor: '#775DD0',
        label: {
          text: firstText,
          style: {
            color: '#fff',
            background: '#775DD0',
          },
        },
      },
      isSameDay
        ? {}
        : {
            x: endFormatted,
            borderColor: '#775DD0',
            label: {
              text: t('common.endDate'),
              style: {
                color: '#fff',
                background: '#775DD0',
              },
            },
          },
    ]
  }
  return [{}]
}

const getGroupBy = (
  startTime: Exclude<dayjs.ConfigType, null | undefined>,
  endTime: Exclude<dayjs.ConfigType, null | undefined>,
): GroupByDate => {
  if (!startTime || !endTime) {
    return GroupByDate.DAY
  }
  const daysBetween = dayjs(endTime).diff(dayjs(startTime), 'day')
  let groupBy: GroupByDate = GroupByDate.DAY
  const maxTicks = 18
  if (daysBetween > 7 * maxTicks) {
    groupBy = GroupByDate.MONTH
  }
  return groupBy
}

const getShortDateFormatFromGroupBy = (granularity: GroupByDate): string => {
  switch (granularity) {
    case GroupByDate.DAY:
      return 'MMM D'
    case GroupByDate.WEEK:
      return 'MMM D, YYYY' // Not Used
    case GroupByDate.MONTH:
      return "MMM 'YY"
    case GroupByDate.YEAR:
      return 'YYYY'
    default:
      return 'MMM DD, YYYY'
  }
}

const getLongDateFormatFromGroupBy = (granularity: GroupByDate): string => {
  switch (granularity) {
    case GroupByDate.DAY:
      return 'ddd MMM. D, YYYY'
    case GroupByDate.WEEK:
      return 'MMM D, YYYY' // Not Used
    case GroupByDate.MONTH:
      return 'MMMM YYYY'
    case GroupByDate.YEAR:
      return 'YYYY'
    default:
      return 'MMM D, YYYY'
  }
}

export const ChartHelpers = {
  getXAxisRangeAnnotation,
  getChartWindow,
  getGroupBy,
  getShortDateFormatFromGroupBy,
  getLongDateFormatFromGroupBy,
  getCompletionPercentage,
  getNumberOfIncompletePeriods,
}
